/**
 * Reading a booking's `driver_overtime` list — the dashboard's copy of the
 * API's Helpers/driverOvertime.helper.ts, so the driver portal, the admin
 * booking screen and the server agree on what "running" means and on how many
 * minutes count.
 *
 * An entry is { stage: 'pickup'|'dropoff', method: 'timer'|'manual',
 * started_at?, ended_at?, minutes?, note? }. A closed timer's minutes are
 * computed server-side when it closes; a manual entry's are as entered.
 * Minutes only — what overtime is worth is priced elsewhere.
 */

/** The entries, always an array (an admin booking may have none). */
export const overtimeEntries = (entries) => entries || [];

/** A timer that has been started and not yet closed. Manual entries never run. */
export const isRunningTimer = (entry) => Boolean(entry)
  && entry.method === 'timer' && !entry.ended_at;

/** The running timer, if any — there is at most one. */
export const findRunningTimer = (entries) => overtimeEntries(entries)
  .find(isRunningTimer) || null;

/** The minutes one entry counts for; null while its timer runs (it has none yet). */
export const entryMinutes = (entry) => (isRunningTimer(entry) ? null : Number(entry?.minutes) || 0);

/** Closed timers plus manual entries. A running timer adds nothing until it closes. */
export const overtimeTotalMinutes = (entries) => overtimeEntries(entries)
  .reduce((total, entry) => total + (entryMinutes(entry) || 0), 0);

/* --- review ---------------------------------------------------------------
 * Each entry is reviewed by staff like a receipt: `review_status` is
 * 'pending' until 'approved' or 'rejected'. Mirrors the API helper: an entry
 * from before reviews existed has no `review_status` and reads as pending; a
 * running timer is pending too, and deciding it is what stops its clock.
 */

const DECIDED = ['approved', 'rejected'];

/** 'pending' | 'approved' | 'rejected' — anything undecided is pending. */
export const overtimeReviewStatus = (entry) => (
  DECIDED.includes(entry?.review_status) ? entry.review_status : 'pending'
);

// An entry written before entries had ids is addressed by its created_at.
const LEGACY_OVERTIME_ID_PREFIX = 'created-';

/**
 * The id the review endpoint takes for an entry: its `_id`, or — for an entry
 * from before ids existed — `created-<ISO created_at>`.
 */
export const overtimeEntryId = (entry) => {
  // eslint-disable-next-line no-underscore-dangle
  if (entry?._id) return String(entry._id);
  const created = entry?.created_at ? new Date(entry.created_at) : null;
  return created && !Number.isNaN(created.valueOf())
    ? `${LEGACY_OVERTIME_ID_PREFIX}${created.toISOString()}`
    : '';
};

/**
 * Still needs staff: a running timer, or an entry nobody has decided.
 *
 * An entry the review endpoint cannot address — no `_id` and no usable
 * `created_at` — is NOT pending: it can never be found, so it can never be
 * decided, and counting it would show a badge that never clears. Mirrors the
 * API helper.
 */
export const isPendingOvertime = (entry) => Boolean(entry)
  && Boolean(overtimeEntryId(entry))
  && (isRunningTimer(entry) || overtimeReviewStatus(entry) === 'pending');

/** How many entries still need staff. */
export const pendingOvertimeCount = (entries) => overtimeEntries(entries)
  .filter(isPendingOvertime).length;
